import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from './lib/axiosClient';

const STATUS = {
  draft: { label: 'Draft', color: '#888', icon: '📝' },
  sent: { label: 'Sent', color: '#0984e3', icon: '📤' },
  partial: { label: 'Partial', color: '#f59e0b', icon: '💰' },
  paid: { label: 'Paid', color: '#00b894', icon: '✅' },
  overdue: { label: 'Overdue', color: '#d63031', icon: '⚠️' },
  void: { label: 'Void', color: '#636e72', icon: '🚫' },
};
var fmtPrice = (p) => '$' + parseFloat(p || 0).toFixed(2);
var fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
var fmtShort = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

export default function Invoices() {
  var [invoices, setInvoices] = useState([]);
  var [customers, setCustomers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('active');
  var [search, setSearch] = useState('');
  var [sortBy, setSortBy] = useState('newest');
  var [showModal, setShowModal] = useState(false);
  var [detail, setDetail] = useState(null);
  var [selectedIds, setSelectedIds] = useState([]);

  var fetchData = useCallback(async () => {
    try {
      var [ir, cr] = await Promise.all([
        axios.get('/api/invoices'),
        axios.get('/api/customers?limit=500').catch(() => ({ data: { customers: [] } })),
      ]);
      setInvoices(ir.data.invoices || []);
      setCustomers(cr.data.customers || cr.data.contacts || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  var openDetail = async (id) => {
    try { var r = await axios.get('/api/invoices/' + id); setDetail(r.data.invoice); }
    catch (e) { alert('Failed to load invoice'); }
  };

  var saveInvoice = async (form) => {
    try {
      if (form.id) await axios.put('/api/invoices/' + form.id, form);
      else await axios.post('/api/invoices', form);
      setShowModal(false); fetchData();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  var sendInvoice = async (id) => {
    try { await axios.post('/api/invoices/' + id + '/send'); openDetail(id); fetchData(); }
    catch (e) { alert('Failed to send'); }
  };

  var recordPayment = async (id, amount, method) => {
    try { await axios.post('/api/invoices/' + id + '/payment', { amount: parseFloat(amount), payment_method: method }); openDetail(id); fetchData(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  var deleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try { await axios.delete('/api/invoices/' + id); setDetail(null); fetchData(); }
    catch (e) { alert('Failed'); }
  };

  var addItem = async (invId, item) => {
    try { await axios.post('/api/invoices/' + invId + '/items', item); openDetail(invId); fetchData(); }
    catch (e) { alert('Failed'); }
  };

  var deleteItem = async (invId, itemId) => {
    try { await axios.delete('/api/invoices/' + invId + '/items/' + itemId); openDetail(invId); fetchData(); }
    catch (e) { alert('Failed'); }
  };

  // Stats
  var outstanding = invoices.filter(i => ['sent', 'partial', 'overdue'].includes(i.status));
  var totalOutstanding = outstanding.reduce((s, i) => s + parseFloat(i.balance_due || 0), 0);
  var totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + parseFloat(i.total || 0), 0);
  var overdueCount = invoices.filter(i => i.status === 'overdue').length;
  var draftCount = invoices.filter(i => i.status === 'draft').length;

  var filtered = useMemo(() => {
    var q = search.toLowerCase();
    var list = invoices.filter(i => {
      var mf = filter === 'active' ? ['draft', 'sent', 'partial', 'overdue'].includes(i.status) :
        filter === 'paid' ? i.status === 'paid' :
        filter === 'overdue' ? i.status === 'overdue' :
        filter === 'draft' ? i.status === 'draft' : true;
      var ms = !q || (i.invoice_number || '').toLowerCase().includes(q) ||
        (i.title || '').toLowerCase().includes(q) ||
        (i.contacts?.first_name || '').toLowerCase().includes(q) ||
        (i.contacts?.last_name || '').toLowerCase().includes(q);
      return mf && ms;
    });
    list.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortBy === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortBy === 'total-high') return parseFloat(b.total || 0) - parseFloat(a.total || 0);
      if (sortBy === 'total-low') return parseFloat(a.total || 0) - parseFloat(b.total || 0);
      if (sortBy === 'balance') return parseFloat(b.balance_due || 0) - parseFloat(a.balance_due || 0);
      if (sortBy === 'due-date') return new Date(a.due_date || '9999') - new Date(b.due_date || '9999');
      return 0;
    });
    return list;
  }, [invoices, filter, search, sortBy]);

  var S = {
    input: { width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', color: '#e2e2e8', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: '600', color: '#888', marginBottom: '6px', display: 'block' },
    card: { background: '#12121f', border: '1px solid #1e1e35', borderRadius: '16px', padding: '20px' },
    pill: (active) => ({ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: active ? '#ff6b2b' : 'transparent', color: active ? '#fff' : '#888' }),
    badge: (color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: color + '20', color }),
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888' }}><p>Loading invoices...</p></div>;

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 6px' }}>Invoices</h1>
          <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>{invoices.length} total · {outstanding.length} outstanding</p>
        </div>
        <button onClick={() => setShowModal(true)} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
          + New Invoice
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Outstanding', value: fmtPrice(totalOutstanding), color: '#f59e0b', click: () => setFilter('active') },
          { label: 'Collected', value: fmtPrice(totalPaid), color: '#00b894', click: () => setFilter('paid') },
          { label: 'Overdue', value: overdueCount, color: overdueCount > 0 ? '#d63031' : '#888', click: () => setFilter('overdue') },
          { label: 'Drafts', value: draftCount, color: '#888', click: () => setFilter('draft') },
          { label: 'Total Invoices', value: invoices.length, color: '#ff6b2b' },
        ].map((s, i) => (
          <div key={i} onClick={s.click} style={{ ...S.card, padding: '16px', cursor: s.click ? 'pointer' : 'default' }}>
            <p style={{ fontSize: '10px', color: '#666', fontWeight: '700', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
            <p style={{ fontSize: '22px', fontWeight: '800', color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '3px', background: '#1a1c2e', borderRadius: '10px', padding: '4px' }}>
          {['active', 'paid', 'overdue', 'draft', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={S.pill(filter === f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
        <input placeholder="Search invoice #, title, customer..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...S.input, maxWidth: '260px', flex: '1 1 200px' }} />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.input, maxWidth: '180px', cursor: 'pointer' }}>
          <option value="newest">Sort: Newest</option>
          <option value="oldest">Sort: Oldest</option>
          <option value="total-high">Sort: Total ↓</option>
          <option value="total-low">Sort: Total ↑</option>
          <option value="balance">Sort: Balance ↓</option>
          <option value="due-date">Sort: Due Date</option>
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 24px', background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35' }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>📄</p>
          <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>{invoices.length === 0 ? 'No invoices yet' : 'No matches'}</p>
          <p style={{ color: '#888', fontSize: '14px' }}>{invoices.length === 0 ? 'Create your first invoice' : 'Try adjusting filters'}</p>
        </div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e1e35' }}>
                  {['Invoice', 'Customer', 'Vehicle', 'Status', 'Due', 'Total', 'Balance', ''].map(h => (
                    <th key={h} style={{ padding: '12px 10px', textAlign: h === 'Total' || h === 'Balance' ? 'right' : 'left', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  var sc = STATUS[inv.status] || STATUS.draft;
                  var bal = parseFloat(inv.balance_due || 0);
                  var isOverdue = inv.status === 'overdue' || (inv.due_date && new Date(inv.due_date) < new Date() && bal > 0 && inv.status !== 'paid');
                  return (
                    <tr key={inv.id} onClick={() => openDetail(inv.id)} style={{ borderBottom: '1px solid #1e1e3515', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#252840'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 10px' }}>
                        <span style={{ color: '#0984e3', fontWeight: '700', fontSize: '13px' }}>{inv.invoice_number}</span>
                        {inv.title && <p style={{ color: '#666', fontSize: '11px', margin: '2px 0 0', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.title}</p>}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        {inv.contacts ? <span style={{ color: '#aaa', fontSize: '13px' }}>{inv.contacts.first_name} {inv.contacts.last_name}</span> : <span style={{ color: '#444' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        {inv.vehicles ? <span style={{ color: '#888', fontSize: '12px' }}>🏍️ {inv.vehicles.year} {inv.vehicles.make} {inv.vehicles.model}</span> : <span style={{ color: '#444', fontSize: '12px' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 10px' }}><span style={S.badge(sc.color)}>{sc.icon} {sc.label}</span></td>
                      <td style={{ padding: '12px 10px' }}>
                        <span style={{ color: isOverdue ? '#d63031' : '#888', fontSize: '12px', fontWeight: isOverdue ? '700' : '400' }}>{fmtShort(inv.due_date)}</span>
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'right' }}><span style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>{fmtPrice(inv.total)}</span></td>
                      <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                        <span style={{ color: bal > 0 ? '#d63031' : '#00b894', fontWeight: '800', fontSize: '14px' }}>{fmtPrice(bal)}</span>
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <button onClick={e => { e.stopPropagation(); deleteInvoice(inv.id); }} style={{ padding: '4px 8px', background: '#d6303110', border: 'none', borderRadius: '6px', color: '#d63031', fontSize: '12px', cursor: 'pointer' }}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 14px', borderTop: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
            <span>Showing {filtered.length} of {invoices.length}</span>
            <span style={{ color: '#f59e0b' }}>Outstanding: {fmtPrice(totalOutstanding)}</span>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && <CreateModal customers={customers} onSave={saveInvoice} onClose={() => setShowModal(false)} S={S} />}

      {/* Detail Panel */}
      {detail && <DetailPanel inv={detail} onClose={() => setDetail(null)} onSend={() => sendInvoice(detail.id)}
        onPayment={(a, m) => recordPayment(detail.id, a, m)} onDelete={() => deleteInvoice(detail.id)}
        onAddItem={(item) => addItem(detail.id, item)} onDeleteItem={(itemId) => deleteItem(detail.id, itemId)}
        S={S} />}
    </div>
  );
}

// ══════════════════════════════════════════════
// CREATE MODAL
// ══════════════════════════════════════════════
function CreateModal({ customers, onSave, onClose, S }) {
  var [form, setForm] = useState({ contact_id: '', title: '', notes: '', due_date: '' });
  var [custSearch, setCustSearch] = useState('');
  var u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  var fc = customers.filter(c => !custSearch || (c.first_name + ' ' + (c.last_name || '') + ' ' + (c.phone || '')).toLowerCase().includes(custSearch.toLowerCase())).slice(0, 12);
  var sel = customers.find(c => String(c.id) === String(form.contact_id));

  // Default due date: 30 days from now
  useEffect(() => {
    var d = new Date(); d.setDate(d.getDate() + 30);
    u('due_date', d.toISOString().split('T')[0]);
  }, []);

  return (
    <Overlay onClose={onClose}>
      <div style={{ padding: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: 0 }}>New Invoice</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gap: '14px' }}>
          <div>
            <label style={S.label}>Customer *</label>
            {sel && !custSearch ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ ...S.input, flex: 1, borderColor: '#00b894', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#fff' }}>{sel.first_name} {sel.last_name}</span>
                  <span style={{ color: '#00b894' }}>✓</span>
                </div>
                <button onClick={() => { u('contact_id', ''); setCustSearch(' '); }} style={{ padding: '12px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', cursor: 'pointer', fontSize: '12px' }}>Change</button>
              </div>
            ) : (
              <div>
                <input placeholder="Search customer..." value={custSearch} onChange={e => setCustSearch(e.target.value)} style={S.input} autoFocus />
                <div style={{ maxHeight: '140px', overflowY: 'auto', marginTop: '4px' }}>
                  {fc.map(c => (
                    <div key={c.id} onClick={() => { u('contact_id', c.id); setCustSearch(''); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #1e1e3520' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#252840'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>{c.first_name} {c.last_name}</span>
                      <span style={{ color: '#666', fontSize: '12px' }}>{c.phone || ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div><label style={S.label}>Title</label><input value={form.title} onChange={e => u('title', e.target.value)} placeholder="e.g. Engine rebuild — June" style={S.input} /></div>
          <div><label style={S.label}>Due Date</label><input type="date" value={form.due_date} onChange={e => u('due_date', e.target.value)} style={S.input} /></div>
          <div><label style={S.label}>Notes</label><textarea value={form.notes} onChange={e => u('notes', e.target.value)} placeholder="Notes for the customer..." rows={2} style={{ ...S.input, resize: 'vertical' }} /></div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '14px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onSave(form)} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>Create Invoice</button>
        </div>
      </div>
    </Overlay>
  );
}

// ══════════════════════════════════════════════
// DETAIL PANEL
// ══════════════════════════════════════════════
function DetailPanel({ inv, onClose, onSend, onPayment, onDelete, onAddItem, onDeleteItem, S }) {
  var [newItem, setNewItem] = useState({ type: 'labor', name: '', quantity: '1', unit_price: '' });
  var [showAdd, setShowAdd] = useState(false);
  var [payAmount, setPayAmount] = useState('');
  var [payMethod, setPayMethod] = useState('cash');
  var [showPay, setShowPay] = useState(false);
  var sc = STATUS[inv.status] || STATUS.draft;
  var bal = parseFloat(inv.balance_due || 0);
  var paid = parseFloat(inv.amount_paid || 0);
  var total = parseFloat(inv.total || 0);
  var paidPct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;

  var printInvoice = () => {
    var w = window.open('', '_blank', 'width=600,height=800');
    if (!w) return;
    var items = inv.items || [];
    var html = `<html><head><style>
      body{font-family:-apple-system,sans-serif;padding:40px;max-width:560px;margin:0 auto;font-size:13px;color:#222}
      h1{font-size:24px;margin:0 0 4px} .sub{color:#888;font-size:12px;margin-bottom:24px}
      .row{display:flex;justify-content:space-between;padding:4px 0} .bold{font-weight:700} .big{font-size:18px}
      hr{border:none;border-top:1px solid #e5e7eb;margin:16px 0}
      table{width:100%;border-collapse:collapse;margin:16px 0} th{text-align:left;border-bottom:2px solid #ddd;padding:8px 4px;font-size:11px;text-transform:uppercase;color:#888}
      td{padding:8px 4px;border-bottom:1px solid #f3f4f6;font-size:13px} .right{text-align:right}
      .badge{display:inline-block;padding:4px 12px;border-radius:6px;font-weight:700;font-size:12px}
      .footer{margin-top:32px;text-align:center;color:#888;font-size:11px}
      @media print{body{padding:20px}}
    </style></head><body>`;
    html += `<div style="display:flex;justify-content:space-between;align-items:flex-start">`;
    html += `<div><h1>Invoice</h1><div class="sub">${inv.invoice_number}</div></div>`;
    html += `<div style="text-align:right"><span class="badge" style="background:${sc.color}20;color:${sc.color}">${sc.label}</span></div></div>`;
    html += `<hr>`;
    html += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:20px">`;
    html += `<div><div style="font-size:11px;color:#888;text-transform:uppercase;font-weight:700">Bill To</div>`;
    html += `<div style="font-weight:700;font-size:15px;margin-top:4px">${inv.contacts?.first_name || ''} ${inv.contacts?.last_name || ''}</div>`;
    if (inv.contacts?.phone) html += `<div style="color:#666">${inv.contacts.phone}</div>`;
    if (inv.contacts?.email) html += `<div style="color:#666">${inv.contacts.email}</div>`;
    html += `</div>`;
    html += `<div style="text-align:right"><div class="row"><span style="color:#888">Invoice Date:</span><span>${fmtDate(inv.created_at)}</span></div>`;
    if (inv.due_date) html += `<div class="row"><span style="color:#888">Due Date:</span><span style="font-weight:700">${fmtDate(inv.due_date)}</span></div>`;
    html += `</div></div>`;
    if (inv.vehicles) html += `<div style="margin-bottom:16px;padding:8px 12px;background:#f8f8f8;border-radius:8px;font-size:12px">🏍️ ${inv.vehicles.year || ''} ${inv.vehicles.make || ''} ${inv.vehicles.model || ''} ${inv.vehicles.license_plate ? '· ' + inv.vehicles.license_plate : ''}</div>`;
    html += `<table><thead><tr><th>Item</th><th>Type</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr></thead><tbody>`;
    items.forEach(i => { html += `<tr><td style="font-weight:600">${i.name}</td><td>${i.type}</td><td class="right">${i.quantity}</td><td class="right">$${parseFloat(i.unit_price).toFixed(2)}</td><td class="right" style="font-weight:700">$${parseFloat(i.total).toFixed(2)}</td></tr>`; });
    html += `</tbody></table>`;
    html += `<hr>`;
    html += `<div style="max-width:240px;margin-left:auto">`;
    html += `<div class="row"><span>Subtotal</span><span>${fmtPrice(inv.subtotal || inv.total)}</span></div>`;
    if (parseFloat(inv.tax_amount || 0) > 0) html += `<div class="row"><span>Tax</span><span>${fmtPrice(inv.tax_amount)}</span></div>`;
    if (parseFloat(inv.discount_amount || 0) > 0) html += `<div class="row"><span>Discount</span><span>-${fmtPrice(inv.discount_amount)}</span></div>`;
    html += `<div class="row bold big" style="border-top:2px solid #222;padding-top:8px;margin-top:8px"><span>Total</span><span>${fmtPrice(total)}</span></div>`;
    if (paid > 0) html += `<div class="row" style="color:green"><span>Paid</span><span>-${fmtPrice(paid)}</span></div>`;
    if (bal > 0) html += `<div class="row bold" style="color:red;font-size:16px"><span>Balance Due</span><span>${fmtPrice(bal)}</span></div>`;
    html += `</div>`;
    if (inv.notes) html += `<hr><div style="font-size:12px;color:#666"><strong>Notes:</strong> ${inv.notes}</div>`;
    html += `<div class="footer"><p>Thank you for your business!</p></div>`;
    html += `</body></html>`;
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <Overlay onClose={onClose}>
      <div>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <span style={{ color: '#0984e3', fontWeight: '800', fontSize: '15px' }}>{inv.invoice_number}</span>
              <span style={S.badge(sc.color)}>{sc.icon} {sc.label}</span>
            </div>
            <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 4px' }}>{inv.title || 'Untitled Invoice'}</h3>
            {inv.contacts && <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>{inv.contacts.first_name} {inv.contacts.last_name}{inv.contacts.phone ? ' · ' + inv.contacts.phone : ''}</p>}
            {inv.vehicles && <p style={{ color: '#666', fontSize: '12px', margin: '2px 0 0' }}>🏍️ {inv.vehicles.year} {inv.vehicles.make} {inv.vehicles.model}{inv.vehicles.license_plate ? ' · ' + inv.vehicles.license_plate : ''}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          {/* Payment Summary */}
          <div style={{ background: '#1a1c2e', borderRadius: '14px', padding: '18px', margin: '16px 0', border: '1px solid #1e1e35' }}>
            <R label="Total" value={fmtPrice(total)} bold />
            <R label="Paid" value={fmtPrice(paid)} color="#00b894" />
            {/* Progress bar */}
            <div style={{ height: '6px', background: '#1e1e35', borderRadius: '3px', margin: '8px 0', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: paidPct + '%', background: paidPct >= 100 ? '#00b894' : '#f59e0b', borderRadius: '3px', transition: 'width 0.3s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '2px solid #1e1e35' }}>
              <span style={{ color: '#fff', fontSize: '16px', fontWeight: '800' }}>Balance Due</span>
              <span style={{ color: bal > 0 ? '#d63031' : '#00b894', fontSize: '24px', fontWeight: '800' }}>{fmtPrice(bal)}</span>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', fontSize: '12px' }}>
            <span style={{ color: '#888' }}>Created: <strong style={{ color: '#aaa' }}>{fmtDate(inv.created_at)}</strong></span>
            {inv.due_date && <span style={{ color: '#888' }}>Due: <strong style={{ color: (new Date(inv.due_date) < new Date() && bal > 0) ? '#d63031' : '#aaa' }}>{fmtDate(inv.due_date)}</strong></span>}
          </div>

          {/* Line Items */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ color: '#fff', fontSize: '13px', fontWeight: '700', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Line Items</h4>
              {['draft', 'sent'].includes(inv.status) && (
                <button onClick={() => setShowAdd(!showAdd)} style={{ padding: '4px 12px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#ff6b2b', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                  {showAdd ? 'Cancel' : '+ Add Item'}
                </button>
              )}
            </div>

            {showAdd && (
              <div style={{ background: '#1a1c2e', borderRadius: '10px', padding: '12px', marginBottom: '10px', border: '1px dashed #1e1e35' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '8px', marginBottom: '8px' }}>
                  <select value={newItem.type} onChange={e => setNewItem(p => ({ ...p, type: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }}>
                    <option value="labor">Labor</option><option value="part">Part</option><option value="fee">Fee</option>
                  </select>
                  <input placeholder="Item name" value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px' }}>
                  <input type="number" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                  <input type="number" placeholder="Price" value={newItem.unit_price} onChange={e => setNewItem(p => ({ ...p, unit_price: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                  <button onClick={() => { onAddItem(newItem); setNewItem({ type: 'labor', name: '', quantity: '1', unit_price: '' }); setShowAdd(false); }}
                    style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}>Add</button>
                </div>
                {newItem.quantity && newItem.unit_price && <p style={{ color: '#888', fontSize: '11px', marginTop: '6px' }}>Line total: {fmtPrice(parseFloat(newItem.quantity || 0) * parseFloat(newItem.unit_price || 0))}</p>}
              </div>
            )}

            {(inv.items || []).length === 0 && !showAdd && <p style={{ color: '#555', fontSize: '13px', textAlign: 'center', padding: '16px', fontStyle: 'italic' }}>No items yet — add line items to calculate total</p>}

            {(inv.items || []).map(item => {
              var typeColors = { labor: '#0984e3', part: '#00b894', fee: '#f59e0b' };
              var tc = typeColors[item.type] || '#888';
              return (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1e1e3520' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', background: tc + '20', color: tc }}>{item.type}</span>
                    <span style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>{item.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#666', fontSize: '12px' }}>{item.quantity} × {fmtPrice(item.unit_price)}</span>
                    <span style={{ color: '#00b894', fontWeight: '700', fontSize: '13px', minWidth: '60px', textAlign: 'right' }}>{fmtPrice(item.total)}</span>
                    {['draft', 'sent'].includes(inv.status) && (
                      <button onClick={() => onDeleteItem(item.id)} style={{ width: '20px', height: '20px', background: '#d6303115', border: 'none', borderRadius: '4px', color: '#d63031', cursor: 'pointer', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Record Payment */}
          {['sent', 'partial', 'overdue'].includes(inv.status) && bal > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ color: '#fff', fontSize: '13px', fontWeight: '700', margin: 0, textTransform: 'uppercase' }}>Record Payment</h4>
                <button onClick={() => setShowPay(!showPay)} style={{ padding: '4px 12px', background: '#00b89415', border: 'none', borderRadius: '6px', color: '#00b894', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                  {showPay ? 'Cancel' : '💰 Add Payment'}
                </button>
              </div>
              {showPay && (
                <div style={{ background: '#1a1c2e', borderRadius: '10px', padding: '12px', border: '1px dashed #1e1e35' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <div><label style={{ ...S.label, fontSize: '11px' }}>Amount</label><input type="number" placeholder="0.00" value={payAmount} onChange={e => setPayAmount(e.target.value)} style={{ ...S.input, padding: '8px', fontSize: '13px' }} /></div>
                    <div><label style={{ ...S.label, fontSize: '11px' }}>Method</label>
                      <select value={payMethod} onChange={e => setPayMethod(e.target.value)} style={{ ...S.input, padding: '8px', fontSize: '13px', cursor: 'pointer' }}>
                        {['cash', 'card', 'check', 'venmo', 'zelle', 'bank_transfer', 'other'].map(m => <option key={m} value={m}>{m.replace('_', ' ').toUpperCase()}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setPayAmount(bal.toFixed(2))} style={{ flex: 1, padding: '8px', background: '#1e1e35', border: 'none', borderRadius: '8px', color: '#aaa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Pay Full ({fmtPrice(bal)})</button>
                    <button onClick={() => { onPayment(payAmount, payMethod); setPayAmount(''); setShowPay(false); }}
                      style={{ flex: 1, padding: '8px', background: '#00b894', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>Record Payment</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {inv.notes && <div style={{ marginBottom: '16px' }}><h4 style={{ color: '#888', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>Notes</h4><p style={{ color: '#aaa', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>{inv.notes}</p></div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={onDelete} style={{ padding: '10px 16px', background: '#d6303115', border: 'none', borderRadius: '10px', color: '#d63031', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>Delete</button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={printInvoice} style={{ padding: '10px 16px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>🖨️ Print</button>
            {inv.status === 'draft' && (
              <button onClick={onSend} style={{ padding: '10px 16px', background: '#0984e3', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>📤 Mark Sent</button>
            )}
          </div>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '30px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>{children}</div>
    </div>
  );
}

function R({ label, value, color, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: '#888', fontSize: '13px', fontWeight: bold ? '700' : '400' }}>{label}</span>
      <span style={{ color: color || '#aaa', fontSize: bold ? '16px' : '13px', fontWeight: bold ? '800' : '600' }}>{value}</span>
    </div>
  );
}
